import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TextField } from '@cfa/react-components';

import ConfirmationModal from '@/components/popups/ConfirmationModal';
import { PathwayModal } from '@/components/popups/PathwayModal';

import { LanguageObject } from '@/types';

import { getNameFromLanguage } from 'utils/language';

type RenameStep = 'rename' | 'invalid';

interface RenameQuizPopupProps {
  showRenameQuizPopup: boolean;
  setShowRenameQuizPopup: (show: boolean) => void;
  /**
   * Caller closes the dialog on success. Returning `false` (e.g. API error)
   * leaves the rename step open — blank names are handled in this popup.
   */
  onRenameQuizAndClosePopup: (
    inputValue: string,
  ) => boolean | undefined | Promise<boolean | undefined>;
  /** When false, Update stays disabled until quiz details have loaded. */
  isDetailsReady?: boolean;
  quizDetails: { name: LanguageObject };
}

const RenameQuizPopup: React.FC<RenameQuizPopupProps> = ({
  showRenameQuizPopup,
  setShowRenameQuizPopup,
  onRenameQuizAndClosePopup,
  isDetailsReady = true,
  quizDetails,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<RenameStep>('rename');
  const [textInputValue, setTextInputValue] = useState<string>();
  const [wasOpen, setWasOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const defaultValue = getNameFromLanguage(quizDetails.name);
  const clampedDefaultValue = defaultValue.slice(0, 250);
  const fieldValue = textInputValue ?? clampedDefaultValue;

  const isDisabled =
    isSubmitting ||
    !isDetailsReady ||
    !fieldValue.trim() ||
    fieldValue === clampedDefaultValue;

  if (showRenameQuizPopup !== wasOpen) {
    setWasOpen(showRenameQuizPopup);
    if (showRenameQuizPopup) {
      setStep('rename');
      setTextInputValue(undefined);
    }
  }

  useEffect(() => {
    if (!showRenameQuizPopup || step !== 'rename') {
      return;
    }
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [showRenameQuizPopup, step]);

  const closePopup = () => {
    setStep('rename');
    setTextInputValue(undefined);
    setShowRenameQuizPopup(false);
  };

  const submitRename = async () => {
    const value = fieldValue.trim();
    if (!value) {
      setStep('invalid');
      return;
    }

    setIsSubmitting(true);
    try {
      await onRenameQuizAndClosePopup(value);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PathwayModal
      isOpen={showRenameQuizPopup}
      onClose={closePopup}
      className="2xl:max-w-[610px]!"
      stepKey={step}
    >
      <PathwayModal.Step stepKey="rename">
        <PathwayModal.Header>
          <PathwayModal.Title>
            {t('Generic.giveNewName', { object: t('Generic.quiz') })}
          </PathwayModal.Title>
        </PathwayModal.Header>
        <PathwayModal.Body>
          <TextField
            ref={inputRef}
            data-testid="ModalInput"
            value={fieldValue}
            fullWidth={true}
            maxLength={250}
            onChange={e => setTextInputValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !isDisabled) {
                void submitRename();
              }
            }}
          />
        </PathwayModal.Body>
        <PathwayModal.Footer>
          <PathwayModal.SecondaryButton
            onPress={closePopup}
            testId="ConfirmationPopUpSecondaryButton"
          >
            {t('Button.cancel')}
          </PathwayModal.SecondaryButton>
          <PathwayModal.PrimaryButton
            isDisabled={isDisabled}
            onPress={() => {
              void submitRename();
            }}
            testId="ConfirmationPopUpPrimaryButton"
            variant="filled"
          >
            {t('Button.update')}
          </PathwayModal.PrimaryButton>
        </PathwayModal.Footer>
      </PathwayModal.Step>

      <PathwayModal.Step stepKey="invalid">
        <ConfirmationModal
          isStep
          isError
          bodyText={t('InvalidQuizName.paragraphText')}
          headerText={t('InvalidQuizName.errorHeader')}
          primaryButtonHandler={() => setStep('rename')}
          primaryButtonText={t('Button.close')}
        />
      </PathwayModal.Step>
    </PathwayModal>
  );
};

export default RenameQuizPopup;
